import { fromLonLat } from 'ol/proj'
import { Point, LineString, LinearRing, Geometry, MultiPoint, MultiLineString, MultiPolygon, Polygon, GeometryCollection } from 'ol/geom'
import Feature, { FeatureLike } from 'ol/Feature'
import VectorLayer from 'ol/layer/Vector'
import type Layer from 'ol/layer/Layer'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style, Text } from 'ol/style'
import { useSharedMap } from './use-shared-map'
import Select, { type Options as SelectOptions } from 'ol/interaction/Select'
import { ref, shallowRef } from 'vue'
import { GridCellProps, GridFeature } from '/@/types/map-grid'
import { useSharedMapInteractions } from '/@/components/station/map/use-shared-map-interactions'
import DragPan from 'ol/interaction/DragPan'
import { platformModifierKeyOnly, shiftKeyOnly, singleClick } from 'ol/events/condition'
import MapBrowserEvent from 'ol/MapBrowserEvent'
import { AMapConfig } from '/@/constants'

import OL3Parser from 'jsts/org/locationtech/jts/io/OL3Parser.js'
import UnionOp from 'jsts/org/locationtech/jts/operation/union/UnionOp.js'
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp.js'
import { isNull } from 'lodash'
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory'
import { EventsKey } from 'ol/events'
import { unByKey } from 'ol/Observable'

const endPaintCallback = shallowRef<(() => void) | null>(null)

export function useSharedMapGrid () {
  const { map, attachTo, detach, isLoading, loadingProgress, labelTextDrawStyle } = useSharedMap()
  const { enableDraw, remove, enableSelect, enableDragPan, setMapDragPan, setMapDragZoom, createBoundaryPayload, createFeatureBoundaryPayload, boundaryFeatures } = useSharedMapInteractions()

  const cellSizeRef = ref(0)
  const cols = ref(0)
  const rows = ref(0)

  const selectRef = ref<Select | null>(null)
  const eventKeys = ref<EventsKey[]>()
  let paintSelecting = false
  let paintMoved = false
  let paintOrigin: number[] | null = null

  let hoveredFeature: GridFeature | null = null

  const getHoveredFeature = () => {
    return hoveredFeature
  }

  const setHoveredFeature = (feature: GridFeature | null) => {
    hoveredFeature = feature
  }

  const gridNormalStyle = (feature: FeatureLike, resolution: number):Style | Style[] => {
    const isHovered = feature === hoveredFeature
    const id = feature.get('id')
    const fontSize = getFontSize(resolution, false)

    const style = new Style({
      stroke: new Stroke({
        color: isHovered ? 'rgba(0, 80, 180, 0.95)' : 'rgba(0, 100, 200, 0.65)',
        width: isHovered ? 2 : 1,
      }),
      fill: new Fill({
        color: isHovered ? 'rgba(0, 120, 255, 0.22)' : 'rgba(0, 120, 255, 0.07)',
      }),
    })

    if (isHovered && fontSize > 0) {
      style.setText(new Text({
        text: id,
        font: `bold ${fontSize}px system-ui, sans-serif`,
        fill: new Fill({ color: '#1a1a1a' }),
        // fill: new Fill({ color: 'rgba(0, 80, 180, 0.95)' }),
        stroke: new Stroke({
          color: 'rgba(255,255,255,0.85)',
          width: Math.max(2, fontSize * 0.25)
        }),
        overflow: true
      }))
    }
    return style
  }

  const gridLayer = new VectorLayer({
    style: gridNormalStyle
  })

  const endPaintSelect = (): void => {
    paintSelecting = false
    paintOrigin = null

    const features = selectRef.value?.getFeatures().getArray()
    if (features) boundaryFeatures.value = features

    // keep paintMoved until after a possible click so Select ignores it
    window.setTimeout(() => {
      paintMoved = false
    }, 0)
  }

  const onMouseLeaveHandler = () => {
    endPaintSelect()
    const hoveredFeature = getHoveredFeature()
    if (!hoveredFeature) return
    const prev = hoveredFeature
    setHoveredFeature(null)
    prev.changed()
    const targetEl = map.value?.getTargetElement()
    if (targetEl) targetEl.style.cursor = ''
  }

  const showGridLayer = (): void => {
    // ---------- Parameters ----------
    const startLonLat = [parseFloat(AMapConfig.gridStartLongitude), parseFloat(AMapConfig.gridStartLatitude)]
    const endLonLat = [parseFloat(AMapConfig.gridEndLongitude), parseFloat(AMapConfig.gridEndLatitude)]
    const cellSize = parseFloat(AMapConfig.gridSize) // meters

    const features = generateGrid(startLonLat, endLonLat, cellSize)
    const gridSource = new VectorSource({
      features: features
    })

    gridLayer.setSource(gridSource)

    map.value?.addLayer(gridLayer)

    const select = enableSelect({
      layers: [gridLayer],
      style: gridSelectedStyle
    })

    selectRef.value = select

    const dragPan = enableDragPan({
      condition: function (evt) {
        const pointerType = (evt.originalEvent as PointerEvent).pointerType
        const thisDragPan = this as DragPan

        // Touch: only two fingers pan
        if (pointerType === 'touch') {
          return thisDragPan.getPointerCount() === 2
        }

        // Mouse: pan only when something is already selected and Shift is NOT held
        // if (select) {
        //   const hasSelection = select.getFeatures().getLength() > 0
        //   if (hasSelection) {
        //     return hasSelection && !shiftKeyOnly(evt)
        //   }
        // }
        // Mouse: Ctrl/Cmd + drag pans
        return platformModifierKeyOnly(evt)
      },
    })
    dragPan.setActive(false)

    function addToSelection (feature: GridFeature): void {
      const selected = select.getFeatures()
      if (!selected.getArray().includes(feature)) {
        selected.push(feature)
      }
    }

    function featureAtPixel (pixel: number[]): GridFeature | null {
      return map.value?.forEachFeatureAtPixel(
        pixel,
        (f, layer: Layer) => (layer === gridLayer ? f as GridFeature : undefined),
        { hitTolerance: 0 }
      ) ?? null
    }

    eventKeys.value = []

    paintSelecting = false
    paintMoved = false
    paintOrigin = null

    const pointerDownKey:EventsKey = map.value?.on('pointerdown', (evt: MapBrowserEvent<PointerEvent>) => {
      if (!evt.originalEvent || (evt.originalEvent as PointerEvent).button !== 0) return
      paintSelecting = true
      paintMoved = false
      paintOrigin = evt.pixel.slice()
    }) as EventsKey

    eventKeys.value.push(pointerDownKey)

    const pointerMoveKey = map.value?.on('pointermove', (evt: MapBrowserEvent<PointerEvent>) => {
      if (platformModifierKeyOnly(evt)) {
        dragPan.setActive(true)
      } else {
        const feature = featureAtPixel(evt.pixel)
        setHovered(feature)

        if (!paintSelecting || !paintOrigin) return

        const dx = evt.pixel[0] - paintOrigin[0]
        const dy = evt.pixel[1] - paintOrigin[1]
        if (!paintMoved && Math.hypot(dx, dy) < 4) return

        if (!paintMoved) {
          paintMoved = true
          if (!shiftKeyOnly(evt)) {
            select.getFeatures().clear()
          }
        }

        if (feature) addToSelection(feature)
      }
    }) as EventsKey

    eventKeys.value.push(pointerMoveKey)

    const pointerUpKey = map.value?.on('pointerup', () => {
      endPaintSelect()
      endPaintCallback.value?.()
    }) as EventsKey

    eventKeys.value.push(pointerUpKey)

    map.value?.getViewport().addEventListener('mouseleave', onMouseLeaveHandler)

    setMapDragPan(false)
    setMapDragZoom(false)
  }

  const removeGridLayer = () => {
    // eventKeys.value?.forEach((k) => unByKey(k))
    map.value?.removeLayer(gridLayer)
    selectRef.value = null
    remove('select')
    remove('dragPan')
    endPaintCallback.value = null
    setMapDragPan(true)
    setMapDragZoom(true)
  }

  const clearGridSelectFeatures = () => {
    selectRef.value?.getFeatures().clear()
  }

  function setHovered (next: GridFeature | null): void {
    const targetEl = map.value?.getTargetElement()
    if (!targetEl) return

    targetEl.style.cursor = next ? 'pointer' : ''
    if (next === hoveredFeature) return
    const prev = hoveredFeature
    hoveredFeature = next
    prev?.changed()
    next?.changed()
  }

  // ---------- Helpers ----------
  /** Convert 0-based index to Excel-style letters: 0→A, 25→Z, 26→AA, ... */
  function indexToLetter (n: number) {
    let s = ''
    while (n >= 0) {
      s = String.fromCharCode(65 + (n % 26)) + s
      n = Math.floor(n / 26) - 1
    }
    return s
  }

  const generateGrid = (startLonLat:number[], endLonLat:number[], cellSize: number):Feature[] => {
    // ---------- 1. Project to EPSG:3857 and build extent ----------
    const start = fromLonLat(startLonLat)
    const end = fromLonLat(endLonLat)

    const minX = Math.min(start[0], end[0])
    const maxX = Math.max(start[0], end[0])
    const minY = Math.min(start[1], end[1])
    const maxY = Math.max(start[1], end[1])

    cols.value = Math.ceil((maxX - minX) / cellSize)
    rows.value = Math.ceil((maxY - minY) / cellSize)
    // ---------- 2. Create the grid of polygon features ----------
    // r = 0 is the northernmost (top) row → letter A
    // c = 0 is the westernmost (left) column → number 1
    const features = []
    for (let r = 0; r < rows.value; r++) {
      for (let c = 0; c < cols.value; c++) {
        // top edge of this cell (from north)
        const y1 = maxY - r * cellSize
        const y0 = y1 - cellSize
        const x0 = minX + c * cellSize
        const x1 = x0 + cellSize

        const polygon = new Polygon([[
          [x0, y0],
          [x1, y0],
          [x1, y1],
          [x0, y1],
          [x0, y0]
        ]])

        const letter = indexToLetter(r)
        const number = c + 1
        const id = `${letter}-${number}`

        const props: GridCellProps = {
          row: r,
          col: c,
          id: id,
        }

        const feature = new Feature({
          geometry: polygon,
          ...props,
        }) as GridFeature

        features.push(feature)
      }
    }
    cellSizeRef.value = cellSize
    return features
  }

  // ---------- 3. Styles (polygon + dynamically scaled text label) ----------
  /**
     * Calculate font size from map resolution so labels scale with zoom.
     * cellSize is 50 m → cellPixels = 50 / resolution
     * We keep labels readable and hide them when cells become too small.
     */
  function getFontSize (resolution:number, isSelected:boolean) {
    const cellSize = cellSizeRef.value
    const cellPixels = cellSize / resolution
    // Hide labels when a cell is narrower than ~28 px
    if (cellPixels < 28) return 0

    // Target ~22 % of cell width, clamped
    let size = cellPixels * 0.22
    size = Math.max(9, Math.min(isSelected ? 18 : 16, size))
    return Math.round(size)
  }

  const gridSelectedStyle = (feature: FeatureLike, resolution: number) => {
    const id = feature.get('id')
    const fontSize = getFontSize(resolution, true)

    const style = new Style({
      stroke: new Stroke({
        color: '#e63946',
        width: 2.5
      }),
      fill: new Fill({
        color: 'rgba(230, 57, 70, 0.4)'
      })
    })

    if (fontSize > 0) {
      style.setText(new Text({
        text: id,
        font: `bold ${fontSize}px system-ui, sans-serif`,
        fill: new Fill({ color: '#fff' }),
        stroke: new Stroke({
          color: '#9b2226',
          width: Math.max(2, fontSize * 0.25)
        }),
        overflow: true
      }))
    }
    return style
  }

  /** Dissolve Feature[] → one Feature (Polygon or MultiPolygon if disjoint). */
  function unionFeaturesJsts (
    features: Feature<Geometry>[],
  ): Feature<Polygon | MultiPolygon> | null {
    const parser = new OL3Parser(new GeometryFactory(), {
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon,
      GeometryCollection,
    })

    parser.inject(
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon,
      GeometryCollection,
    )

    let unionGeom:any = null
    features.forEach((f:Feature, i:number) => {
      const g = f.getGeometry()
      const jG = BufferOp.bufferOp(parser.read(g), 0)
      if (i === 0) {
        unionGeom = jG
      } else {
        unionGeom = UnionOp.union(unionGeom, jG)
      }
    }
    )

    if (!unionGeom || unionGeom.isEmpty()) return null

    const olGeom = parser.write(unionGeom) as Polygon | MultiPolygon
    return new Feature(olGeom)
  }

  return {
    endPaintCallback,
    getHoveredFeature,
    setHoveredFeature,
    showGridLayer,
    removeGridLayer,
    clearGridSelectFeatures,
    setHovered,
    generateGrid,
    gridNormalStyle,
    gridSelectedStyle,
    unionFeaturesJsts
  }
}
